import React, { FC } from 'react';
import { t } from '@lingui/macro';
import styled from 'styled-components';

import { Box, Flex } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import { Elevation } from '@rover/kibble/styles';
import { useTheme } from '@rover/kibble/themes';
import CalloutBadge from '@rover/react-lib/src/components/callouts/CalloutBadge';
import TrainingUrls from '@rover/react-lib/src/pages/training/constants/urls';
import useInTrainingPilot from '@rover/react-lib/src/pages/training/hooks/useInTrainingPilot';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useServiceLinksShim from '@rover/shared/js/hooks/useServiceLinksShim';
import { ICON_FOR_SERVICE_LANDING_ENUM, ServiceLandingEnum } from '@rover/types/src/ServiceLanding';

import { getServiceLinkDisplayName, NAVBAR_SERVICE_OPTIONS } from '../../constants';

import DesktopOurServicesItem from './DesktopOurServicesItem';

export type Props = {
  showServices: boolean;
};

const DesktopOurServicesWrapper = styled(Box)<{
  showServices: boolean;
  space0x: string;
  space8x: string;
}>`
  ${Elevation(2)};

  visibility: ${({ showServices }) => (showServices ? 'visible' : 'hidden')};
  transform: ${({ showServices, space0x, space8x }) =>
    showServices ? `translateY(calc(100% + ${space0x}))` : `translateY(calc(100% - ${space8x}))`};
  opacity: ${({ showServices }) => (showServices ? 1 : 0)};
  pointer-events: ${({ showServices }) => (showServices ? 'all' : 'none')};
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  will-change: transform;
`;

const StyledCalloutBadge = styled(CalloutBadge)`
  position: absolute;
  right: -15%;
  top: 15%;
`;

const DesktopOurServices: FC<Props> = ({ showServices }) => {
  const { colors, space } = useTheme();
  const { serviceLinksForCurrentRoute: serviceLinks } = useServiceLinksShim();
  const { isAuthenticated } = useCurrentUserShim();
  const { navigationIsEU } = useCountryConfigurationShim();
  const { shouldShowTrainingEntrypoints } = useInTrainingPilot();

  if (!serviceLinks || isAuthenticated) return null;

  return (
    <DesktopOurServicesWrapper
      showServices={showServices}
      position="absolute"
      top="0x"
      right="0x"
      bottom="0x"
      left="0x"
      display={['none', 'none', 'block']}
      space0x={space['0x']}
      space8x={space['8x']}
    >
      <Section background="primary">
        <Flex height="130px" justifyContent="space-around" alignItems="center">
          {NAVBAR_SERVICE_OPTIONS.map((serviceLandingType) => {
            const isTraining = serviceLandingType === ServiceLandingEnum.DOG_TRAINING;
            const serviceExistsForCountry = serviceLandingType in serviceLinks;

            if (!serviceExistsForCountry) {
              return null;
            }
            if (isTraining && (navigationIsEU || !shouldShowTrainingEntrypoints)) {
              return null;
            }

            const ServiceIcon = ICON_FOR_SERVICE_LANDING_ENUM[serviceLandingType];
            const serviceLinkHref = isTraining
              ? TrainingUrls.SEARCH
              : serviceLinks[serviceLandingType];

            return (
              <Flex alignSelf="stretch" position="relative" key={serviceLandingType}>
                <DesktopOurServicesItem href={serviceLinkHref}>
                  <ServiceIcon fill={colors.brand.rover.toString()} height="32px" />
                  {getServiceLinkDisplayName(serviceLandingType)}
                </DesktopOurServicesItem>
                {isTraining && <StyledCalloutBadge value={t`NEW`} />}
              </Flex>
            );
          })}
        </Flex>
      </Section>
    </DesktopOurServicesWrapper>
  );
};

export default DesktopOurServices;
